<template>
  <div 
    :class="[
      'relative flex items-center w-full h-10 rounded-md shadow-sm border bg-white',
      { 'bg-gray-50': disabled },
      { 'border-red-300 bg-red-50': hasError },
      !hasError ? 'border-gray-300' : '',
      !hasError ? (props.userRole === 'customer' 
                 ? 'focus-within:ring-green-500 focus-within:border-green-500' 
                 : 'focus-within:ring-primary-500 focus-within:border-primary-500')
               : 'focus-within:ring-red-500 focus-within:border-red-500',
      !disabled && !hasError ? 'focus-within:ring-2' : ''
    ]"
  >
    <!-- Icon Slot/Section -->
    <span 
      v-if="icon"
      :class="[
        'flex items-center justify-center h-full px-3 text-gray-500 rounded-l-md border-r shrink-0',
        !hasError ? 'bg-gray-50 border-gray-300' : 'bg-red-50 border-red-300',
        { 'cursor-pointer hover:bg-gray-100': !disabled && iconClickHandler }
      ]"
      @click="handleIconClick"
    >
      <Icon :icon="icon" class="w-5 h-5" />
    </span>
    
    <!-- Slotted Input -->
    <div class="flex-grow h-full min-w-0">
      <slot :hasIcon="!!icon" :hasError="!!hasError"></slot>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@iconify/vue';

const props = defineProps({
  icon: {
    type: String,
    default: '' // e.g., 'mdi:calendar', 'mdi:clock-outline'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  iconClickHandler: {
    type: Function,
    default: null
  },
  userRole: {
    type: String,
    default: 'lender', // 'lender' uses primary-xxx classes, 'customer' uses green-xxx
    validator: (value) => ['customer', 'lender'].includes(value)
  },
  hasError: {
    type: Boolean,
    default: false
  }
});

const handleIconClick = () => {
  if (!props.disabled && props.iconClickHandler) {
    props.iconClickHandler();
  }
};
</script>

<style scoped>
/* Slotted elements take up full space of their container */
:slotted(input),
:slotted(select) {
  @apply w-full h-full bg-transparent focus:outline-none border-none ring-0 focus:ring-0 !important;
  @apply px-3 py-2 text-base sm:text-sm; /* Standard input padding and text size */
  /* Conditional rounding is key here */
}

/* Add specific styles for userRole */
:deep(input:focus),
:deep(select:focus) {
  @apply ring-0 border-none outline-none !important;
}

/* Error state overrides background and keeps transparent */
:slotted(input),
:slotted(select) {
  @apply bg-transparent !important;
}

/* 
  The ideal way to handle conditional rounding (e.g., rounded-l-md if no icon, rounded-l-none if icon)
  is for the slotted element ITSELF to have logic or classes passed to it. 
  Trying to style the ::slotted element based on the parent's prop (icon) from parent CSS is not possible.

  The slot prop `hasIcon` can be used by the parent component to conditionally apply classes to the 
  element being slotted in.

  Example usage in parent:
  <InputWithIcon :icon="myIcon">
    <template #default="{ hasIcon }">
      <input :class="{ 'rounded-l-none': hasIcon, 'rounded-l-md': !hasIcon, 'rounded-r-md': true }" />
    </template>
  </InputWithIcon>

  Alternatively, if the slotted element is always simple (like a single input or select),
  the parent component (`ResponsiveDateInput` or `SharedCancellationReasonStep`)
  can simply ensure the slotted element has `rounded-r-md` if an icon is present in `InputWithIcon`,
  and `rounded-md` if no icon is present in `InputWithIcon` (when `InputWithIcon` is used for plain inputs).
*/
</style> 